/**
 * Persisted application state: dashboards, widget layouts and settings.
 *
 * Everything here is structural and changes rarely, so it lives in React state
 * and localStorage. Live signal values deliberately do not -- they go through
 * the render loop instead, so a 20 Hz data stream never re-renders the tree.
 */
import {
  DEFAULT_VEHICLE, LEARNED_PARTS, normalizeVin, sanitizeIdentity,
  type JourneyScope, type LearnedPart, type LearnedState, type VehicleIdentity, type VehicleProfile,
} from '@obd/core';

export type WidgetType =
  | 'gauge'
  | 'bar'
  | 'number'
  | 'line'
  | 'light'
  | 'score'
  | 'gear'
  | 'shift';

export interface Widget {
  id: string;
  type: WidgetType;
  /** One signal for most types; a line chart can plot several. */
  signals: string[];
  title?: string;
  /** Grid position and span, in columns and rows. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Range overrides; fall back to the signal definition's hints. */
  min?: number;
  max?: number;
  /** Poll rate for this widget's signals, in ms. */
  intervalMs?: number;
  /** Seconds of history a line chart shows. */
  windowS?: number;
  /** Draw a small history trace beneath a numeric readout. */
  sparkline?: boolean;
  /**
   * Which span to show: the live reading, or the figure so far this drive,
   * since the last fill-up, or over the car's whole life. Absent means live.
   */
  scope?: WidgetScope;
}

export type WidgetScope = 'now' | JourneyScope;

/** Grid geometry, separated out because it varies per orientation. */
export interface Geom {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Dashboard {
  id: string;
  name: string;
  columns: number;
  /** Geometry here is the wide (landscape / desktop) layout. */
  widgets: Widget[];
  /**
   * Per-widget geometry for narrow viewports, keyed by widget id.
   *
   * Absent until the user actually edits something in portrait, at which point
   * the derived layout is materialised and becomes theirs. Until then portrait
   * is generated on the fly, so a dashboard built in landscape is immediately
   * usable held upright without any second layout to maintain.
   */
  portrait?: Record<string, Geom>;
  /**
   * Scale row height so the whole dashboard fits the viewport with no
   * scrolling. Essential on a phone in a stand -- you cannot scroll a
   * dashboard while driving.
   */
  fit?: boolean;
}

export interface AppSettings {
  theme: 'dark' | 'light';
  vehicle: VehicleProfile;
  /** Default poll interval for the signals table, in ms. */
  signalsIntervalMs: number;
  /** How many samples the inline sparklines show. */
  sparklinePoints: number;
  /** Hold a screen wake lock while connected. */
  keepAwake: boolean;
  /** Units for speed and distance. */
  units: 'metric' | 'imperial';
  /** Screen dimming in drive mode, 0 (off) to 0.75 (very dark). */
  dimLevel: number;
  /** Orientation to lock to when drive mode is entered. */
  orientationLock: 'auto' | 'landscape' | 'portrait';
  /** Go fullscreen automatically when entering drive mode. */
  fullscreenInDriveMode: boolean;
  /** Try to reconnect automatically if the Bluetooth link drops. */
  autoReconnect: boolean;
  /**
   * Connect to the remembered adapter by itself whenever the app is open and
   * the car is in range, so a drive is logged without tapping anything.
   */
  autoConnect: boolean;
  /**
   * Use the phone's location to correct speed and distance. Off until the
   * driver turns it on, because turning it on is what asks for permission.
   */
  gpsEnabled: boolean;
}

export interface PersistedState {
  version: number;
  dashboards: Dashboard[];
  activeDashboardId: string;
  settings: AppSettings;
  /**
   * What the app has worked out about this car -- gear ratios, accelerator
   * calibration, speed correction -- so it does not start from nothing on
   * every drive.
   */
  learned?: LearnedState;
  /** The car this device was last connected to (the simulator does not count). */
  car?: VehicleIdentity;
  /**
   * Which car each part of `learned` was learned on, so it can be checked
   * against the car in use and carried in exported profiles.
   */
  learnedFrom?: LearnedOwners;
}

export type LearnedOwners = Partial<Record<LearnedPart, VehicleIdentity>>;

/** The key predates schema version 3; the version field inside is what counts. */
const STORAGE_KEY = 'dafer-obd2.state.v2';
const VERSION = 3;
/** Schema version of stored state, dashboards included. */
export const STATE_VERSION = VERSION;

/**
 * Signals that version 3 replaced with a better derived equivalent. The raw
 * ones still exist; dashboards built before simply meant these.
 *
 *   throttle -> accelerator  the raw PID keeps its sensor offset, so a
 *                            released pedal read 15-30%
 *   speed    -> road_speed   the car's reading, corrected by GPS when known
 */
const RENAMED_IN_V3: Record<string, string> = {
  throttle: 'accelerator',
  speed: 'road_speed',
};

export const uid = (): string => Math.random().toString(36).slice(2, 10);

// ------------------------------------------------------------ default layouts

const w = (
  type: WidgetType,
  signals: string[],
  x: number,
  y: number,
  cw: number,
  ch: number,
  extra: Partial<Widget> = {},
): Widget => ({ id: uid(), type, signals, x, y, w: cw, h: ch, ...extra });

/**
 * Four starter dashboards, "Stand" first because that is the primary way this
 * gets used. They reference signals that may not exist on a
 * given car -- widgets for missing signals render as unavailable rather than
 * disappearing, so it stays obvious what the vehicle does not report.
 */
export function defaultDashboards(): Dashboard[] {
  return [
    {
      // Sized for a phone in a windscreen stand: everything on one screen at a
      // glance, nothing to scroll to, large marks readable at arm's length.
      id: uid(),
      name: 'Stand',
      columns: 12,
      fit: true,
      widgets: [
        w('gauge', ['rpm'], 0, 0, 4, 4, { intervalMs: 50 }),
        w('gauge', ['road_speed'], 4, 0, 4, 4, { intervalMs: 50 }),
        w('gear', ['gear'], 8, 0, 2, 2, { intervalMs: 100 }),
        w('shift', ['rpm'], 10, 0, 2, 2, { intervalMs: 50 }),
        w('number', ['consumption'], 8, 2, 2, 2, { intervalMs: 250 }),
        w('number', ['coolant_temp'], 10, 2, 2, 2, { intervalMs: 1000 }),
        w('bar', ['accelerator'], 0, 4, 6, 1, { intervalMs: 50 }),
        w('bar', ['aggression'], 6, 4, 6, 1, { intervalMs: 100 }),
      ],
    },
    {
      id: uid(),
      name: 'Drive',
      columns: 12,
      widgets: [
        w('gauge', ['rpm'], 0, 0, 4, 4, { intervalMs: 50 }),
        w('gauge', ['road_speed'], 4, 0, 4, 4, { intervalMs: 50 }),
        w('gear', ['gear'], 8, 0, 2, 2, { intervalMs: 100 }),
        w('shift', ['rpm'], 10, 0, 2, 2, { intervalMs: 50 }),
        w('number', ['boost'], 8, 2, 2, 2, { intervalMs: 100, sparkline: true }),
        w('number', ['consumption'], 10, 2, 2, 2, { intervalMs: 250, sparkline: true }),
        w('bar', ['accelerator'], 0, 4, 4, 1, { intervalMs: 50 }),
        w('bar', ['engine_load'], 4, 4, 4, 1, { intervalMs: 100 }),
        w('bar', ['aggression'], 8, 4, 4, 1, { intervalMs: 100 }),
        w('line', ['rpm', 'road_speed', 'accelerator'], 0, 5, 12, 4, { intervalMs: 100, windowS: 90 }),
      ],
    },
    {
      id: uid(),
      name: 'Economy',
      columns: 12,
      widgets: [
        w('score', ['score_overall'], 0, 0, 3, 4, { intervalMs: 500 }),
        w('number', ['consumption'], 3, 0, 3, 2, { intervalMs: 250, sparkline: true }),
        w('number', ['consumption_avg'], 6, 0, 3, 2, { intervalMs: 500 }),
        w('number', ['fuel_rate'], 9, 0, 3, 2, { intervalMs: 250, sparkline: true }),
        w('number', ['trip_distance'], 3, 2, 2, 2, { intervalMs: 1000 }),
        w('number', ['trip_fuel'], 5, 2, 2, 2, { intervalMs: 1000 }),
        w('number', ['trip_avg_speed'], 7, 2, 2, 2, { intervalMs: 1000 }),
        w('number', ['range_km'], 9, 2, 3, 2, { intervalMs: 2000 }),
        w('bar', ['trip_coast_pct'], 0, 4, 4, 1, { intervalMs: 1000 }),
        w('bar', ['trip_idle_pct'], 4, 4, 4, 1, { intervalMs: 1000 }),
        w('bar', ['score_smoothness'], 8, 4, 4, 1, { intervalMs: 1000 }),
        w('line', ['consumption', 'road_speed'], 0, 5, 12, 4, { intervalMs: 500, windowS: 300 }),
      ],
    },
    {
      id: uid(),
      name: 'Engine',
      columns: 12,
      widgets: [
        w('number', ['coolant_temp'], 0, 0, 2, 2, { intervalMs: 1000, sparkline: true }),
        w('number', ['oil_temp'], 2, 0, 2, 2, { intervalMs: 1000, sparkline: true }),
        w('number', ['intake_temp'], 4, 0, 2, 2, { intervalMs: 1000 }),
        w('number', ['module_voltage'], 6, 0, 2, 2, { intervalMs: 1000, sparkline: true }),
        w('number', ['timing_advance'], 8, 0, 2, 2, { intervalMs: 100, sparkline: true }),
        w('number', ['maf_effective'], 10, 0, 2, 2, { intervalMs: 100, sparkline: true }),
        w('bar', ['stft_b1'], 0, 2, 3, 1, { intervalMs: 250 }),
        w('bar', ['ltft_b1'], 3, 2, 3, 1, { intervalMs: 1000 }),
        w('number', ['air_fuel_ratio'], 6, 2, 3, 2, { intervalMs: 250, sparkline: true }),
        w('number', ['power_hp'], 9, 2, 3, 2, { intervalMs: 100, sparkline: true }),
        w('line', ['coolant_temp', 'intake_temp', 'oil_temp'], 0, 4, 6, 4, { intervalMs: 1000, windowS: 600 }),
        w('line', ['map', 'maf_effective'], 6, 4, 6, 4, { intervalMs: 100, windowS: 90 }),
      ],
    },
  ];
}

export function defaultSettings(): AppSettings {
  return {
    theme: 'dark',
    vehicle: { ...DEFAULT_VEHICLE },
    signalsIntervalMs: 500,
    sparklinePoints: 60,
    keepAwake: true,
    units: 'metric',
    dimLevel: 0,
    orientationLock: 'auto',
    fullscreenInDriveMode: true,
    autoReconnect: true,
    autoConnect: true,
    gpsEnabled: false,
  };
}

export function defaultState(): PersistedState {
  const dashboards = defaultDashboards();
  return {
    version: VERSION,
    dashboards,
    activeDashboardId: dashboards[0]?.id ?? '',
    settings: defaultSettings(),
  };
}

export function loadState(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const parsed = migrateState(JSON.parse(raw) as PersistedState);
    if (!parsed) return defaultState();
    // Merge settings forward so a new field does not wipe a saved config.
    const state: PersistedState = {
      ...parsed,
      settings: { ...defaultSettings(), ...parsed.settings, vehicle: { ...DEFAULT_VEHICLE, ...parsed.settings?.vehicle } },
    };
    return withCleanCar(state, (parsed as { carVin?: unknown }).carVin);
  } catch {
    return defaultState();
  }
}

/**
 * Clean the stored car identities. Also carries over the plain VIN that the
 * first version of car profiles stored, as the owner of any calibration.
 */
function withCleanCar(state: PersistedState, legacyVin: unknown): PersistedState {
  const { carVin: _legacy, ...rest } = state as PersistedState & { carVin?: unknown };
  const vin = normalizeVin(legacyVin);
  const car = sanitizeIdentity(rest.car) ?? (vin ? { vin } : undefined);
  const owners: LearnedOwners = {};
  for (const part of LEARNED_PARTS) {
    const owner = sanitizeIdentity(rest.learnedFrom?.[part]) ?? (vin ? { vin } : undefined);
    if (owner) owners[part] = owner;
  }
  const next: PersistedState = { ...rest, learnedFrom: owners };
  if (car) next.car = car;
  else delete next.car;
  return next;
}


/**
 * Bring a stored state up to the current schema, or return null if it cannot
 * be. Layouts people have built are worth a migration; an unrecognised shape
 * is not worth guessing at.
 */
export function migrateState(state: PersistedState): PersistedState | null {
  if (!state || !Array.isArray(state.dashboards)) return null;
  if (state.version === VERSION) return state;
  if (state.version !== 2) return null;
  const rename = (keys: string[]): string[] => keys.map((k) => RENAMED_IN_V3[k] ?? k);
  return {
    ...state,
    version: VERSION,
    dashboards: state.dashboards.map((d) => ({
      ...d,
      widgets: d.widgets.map((wd) => ({ ...wd, signals: rename(wd.signals) })),
    })),
  };
}

export function saveState(state: PersistedState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private browsing and full quotas both land here; losing the layout is
    // annoying but must never take the dashboard down with it.
  }
}

/** Every signal a dashboard needs, with the fastest interval anyone asked for. */
export function subscriptionsFor(dashboard: Dashboard | undefined): Map<string, number> {
  const out = new Map<string, number>();
  if (!dashboard) return out;
  for (const widget of dashboard.widgets) {
    const interval = widget.intervalMs ?? 250;
    for (const key of widget.signals) {
      const cur = out.get(key);
      if (cur === undefined || interval < cur) out.set(key, interval);
    }
  }
  return out;
}

/** Find the first free row for a newly added widget. */
export function nextFreeRow(dashboard: Dashboard): number {
  return dashboard.widgets.reduce((max, wd) => Math.max(max, wd.y + wd.h), 0);
}

export const WIDGET_LABELS: Record<WidgetType, string> = {
  gauge: 'Radial gauge',
  bar: 'Horizontal bar',
  number: 'Numeric readout',
  line: 'Line chart',
  light: 'Indicator light',
  score: 'Score ring',
  gear: 'Gear indicator',
  shift: 'Shift light',
};

/** Poll rates offered in the UI, matching the intervals asked for. */
export const INTERVAL_CHOICES: { label: string; ms: number }[] = [
  { label: '0.1 s', ms: 100 },
  { label: '0.5 s', ms: 500 },
  { label: '1 s', ms: 1000 },
  { label: '5 s', ms: 5000 },
  { label: '30 s', ms: 30000 },
];

/** Faster options, offered where a widget genuinely benefits. */
export const FAST_INTERVAL_CHOICES: { label: string; ms: number }[] = [
  { label: '20 ms', ms: 20 },
  { label: '50 ms', ms: 50 },
  ...INTERVAL_CHOICES,
];

// ------------------------------------------------------- responsive layout

/**
 * Columns used when the viewport is portrait.
 *
 * Six divides cleanly into halves and thirds, so two gauges sit side by side
 * and a bar spans the width, without anything landing on a fractional column.
 */
export const PORTRAIT_COLUMNS = 6;

/**
 * How much room each widget type needs in portrait.
 *
 * Chosen so the rendered box is roughly square where the mark is round (gauges,
 * score rings) and wide-and-short where it is a bar. A gauge squeezed into a
 * quarter of a 390px screen is unreadable no matter how the numbers scale, so
 * these are minimums of legibility rather than a proportional rescale of the
 * landscape layout.
 */
const PORTRAIT_SIZES: Record<WidgetType, Geom> = {
  gauge: { x: 0, y: 0, w: 3, h: 3 },
  score: { x: 0, y: 0, w: 3, h: 3 },
  gear: { x: 0, y: 0, w: 2, h: 2 },
  shift: { x: 0, y: 0, w: 4, h: 2 },
  number: { x: 0, y: 0, w: 3, h: 2 },
  bar: { x: 0, y: 0, w: 6, h: 1 },
  light: { x: 0, y: 0, w: 6, h: 1 },
  line: { x: 0, y: 0, w: 6, h: 4 },
};

/**
 * Reflow a wide layout into a portrait one.
 *
 * Widgets keep their reading order from the landscape arrangement (top to
 * bottom, then left to right) and are packed into rows, wrapping when the next
 * one will not fit. Deterministic, so the same dashboard always reflows the
 * same way.
 */
export function derivePortraitLayout(widgets: Widget[]): Record<string, Geom> {
  const ordered = [...widgets].sort((a, b) => a.y - b.y || a.x - b.x);
  const out: Record<string, Geom> = {};
  let x = 0;
  let y = 0;
  let rowHeight = 0;

  for (const widget of ordered) {
    const size = PORTRAIT_SIZES[widget.type] ?? { x: 0, y: 0, w: 3, h: 2 };
    const w = Math.min(size.w, PORTRAIT_COLUMNS);
    if (x + w > PORTRAIT_COLUMNS) {
      y += rowHeight;
      x = 0;
      rowHeight = 0;
    }
    out[widget.id] = { x, y, w, h: size.h };
    x += w;
    rowHeight = Math.max(rowHeight, size.h);
    if (x >= PORTRAIT_COLUMNS) {
      y += rowHeight;
      x = 0;
      rowHeight = 0;
    }
  }
  return out;
}

export interface ResolvedLayout {
  widgets: Widget[];
  columns: number;
  /** True when the portrait arrangement is in use. */
  narrow: boolean;
}

/** The geometry to actually render, for the current viewport shape. */
export function resolveLayout(dashboard: Dashboard, narrow: boolean): ResolvedLayout {
  if (!narrow) {
    return { widgets: dashboard.widgets, columns: dashboard.columns, narrow: false };
  }

  const stored = dashboard.portrait;
  if (!stored) {
    const derived = derivePortraitLayout(dashboard.widgets);
    return {
      widgets: dashboard.widgets.map((w) => ({ ...w, ...(derived[w.id] ?? {}) })),
      columns: PORTRAIT_COLUMNS,
      narrow: true,
    };
  }

  // A customised portrait layout will not know about widgets added since, so
  // those are stacked full width underneath rather than dropped on top of
  // something the user positioned deliberately.
  let nextY = Object.values(stored).reduce((max, g) => Math.max(max, g.y + g.h), 0);
  const widgets = dashboard.widgets.map((w) => {
    const geom = stored[w.id];
    if (geom) return { ...w, ...geom };
    const size = PORTRAIT_SIZES[w.type] ?? { x: 0, y: 0, w: 3, h: 2 };
    const placed = { ...w, x: 0, y: nextY, w: PORTRAIT_COLUMNS, h: size.h };
    nextY += size.h;
    return placed;
  });

  return { widgets, columns: PORTRAIT_COLUMNS, narrow: true };
}

/** Materialise the current portrait geometry so it can be edited in place. */
export function materialisePortrait(dashboard: Dashboard): Record<string, Geom> {
  const resolved = resolveLayout(dashboard, true);
  const out: Record<string, Geom> = {};
  for (const w of resolved.widgets) out[w.id] = { x: w.x, y: w.y, w: w.w, h: w.h };
  return out;
}
